import { useEffect, useState } from "react";
import { api, formatDateTime } from "../lib/api";
import { ACTIVITY_TYPES } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, Trash, ClockCounterClockwise, ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import { StageBadge } from "../components/StageBadge";
import { PhysicianDetailSheet } from "../components/PhysicianDetailSheet";
import { toast } from "sonner";

export default function Activities() {
  const [activities, setActivities] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterRep, setFilterRep] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ physician_id: "", activity_type: "Phone Call", date: new Date().toISOString().slice(0,16), bd_rep: "", notes: "", next_step: "" });
  const [selectedDetail, setSelectedDetail] = useState(null);

  const load = async () => {
    const params = {};
    if (filterType !== "all") params.activity_type = filterType;
    if (filterRep) params.bd_rep = filterRep;
    const [a, p] = await Promise.all([
      api.get("/activities", { params }),
      api.get("/physicians"),
    ]);
    setActivities(a.data);
    setPhysicians(p.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, filterRep]);

  const filtered = search ? activities.filter(a => {
    const s = search.toLowerCase();
    return a.physician_name?.toLowerCase().includes(s) || a.notes?.toLowerCase().includes(s) || a.practice_name?.toLowerCase().includes(s);
  }) : activities;

  const save = async () => {
    if (!form.physician_id || !form.activity_type) { toast.error("Physician & activity type required"); return; }
    await api.post("/activities", { ...form, date: new Date(form.date).toISOString() });
    toast.success("Activity logged");
    setOpen(false);
    setForm({ physician_id: "", activity_type: "Phone Call", date: new Date().toISOString().slice(0,16), bd_rep: form.bd_rep, notes: "", next_step: "" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    await api.delete(`/activities/${id}`);
    load();
  };

  return (
    <div className="p-8" data-testid="activities-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-1">BD Pipeline</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Activity Log</h1>
          <p className="text-sm text-[#5C5E66] mt-2">Chronological log of every BD touchpoint — calls, meetings, emails, LOIs. Each entry links back to the physician.</p>
        </div>
        <Button data-testid="log-activity-btn" onClick={() => setOpen(true)} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none gap-2">
          <Plus size={16} weight="bold" /> Log Activity
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5E66]" />
          <Input data-testid="activity-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search physician, clinic, notes..." className="rounded-none pl-9" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger data-testid="filter-type" className="rounded-none w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Activity Types</SelectItem>
            {ACTIVITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="filter-rep" value={filterRep} onChange={(e) => setFilterRep(e.target.value)} placeholder="BD Rep filter..." className="rounded-none w-40" />
      </div>

      <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-2">{filtered.length} activities</div>

      <div className="bg-white border border-[#E2E2E5]" data-testid="activities-table">
        <div className="grid grid-cols-[140px_1.4fr_140px_1fr_1.4fr_1.2fr_120px_50px] gap-0 border-b border-[#E2E2E5] bg-[#F0F0F2]">
          <Th>Date</Th><Th>Physician / Clinic</Th><Th>Activity</Th><Th>BD Rep</Th><Th>Notes</Th><Th>Next Step</Th><Th>Stage</Th><Th></Th>
        </div>
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-[#5C5E66]">
            <ClockCounterClockwise size={36} weight="light" className="mx-auto mb-2" />
            <div className="text-sm">No activities yet — log your first interaction</div>
          </div>
        ) : filtered.map((a) => (
          <div key={a.id} data-testid={`activity-${a.id}`} className="grid grid-cols-[140px_1.4fr_140px_1fr_1.4fr_1.2fr_120px_50px] gap-0 border-b border-[#E2E2E5] last:border-0 row-hover items-center">
            <Td className="text-xs font-mono text-[#5C5E66]">{formatDateTime(a.date)}</Td>
            <Td onClick={() => setSelectedDetail(a.physician_id)} className="cursor-pointer">
              <div className="font-medium truncate text-sm">{a.physician_name || "—"}</div>
              <div className="text-[10px] text-[#5C5E66] truncate">{a.practice_name || ""}</div>
            </Td>
            <Td><span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-[#006CFF14] text-[#006CFF] border border-[#006CFF33]">{a.activity_type}</span></Td>
            <Td className="text-xs">{a.bd_rep || "—"}</Td>
            <Td className="text-xs text-[#0A0B0F]"><div className="line-clamp-2">{a.notes || "—"}</div></Td>
            <Td className="text-xs text-[#5C5E66]"><div className="flex items-start gap-1 line-clamp-2">{a.next_step && <ArrowRight size={12} className="mt-0.5 flex-shrink-0 text-[#FF9900]" />}{a.next_step || "—"}</div></Td>
            <Td>{a.stage && <StageBadge stage={a.stage} />}</Td>
            <Td>
              <button onClick={() => remove(a.id)} data-testid={`delete-activity-${a.id}`} className="p-1.5 hover:bg-[#C6282810] text-[#5C5E66] hover:text-[#C62828]"><Trash size={14} /></button>
            </Td>
          </div>
        ))}
      </div>

      {/* Log Activity Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl rounded-none border-[#E2E2E5]" data-testid="log-activity-dialog">
          <DialogHeader className="border-b border-[#E2E2E5] pb-3">
            <DialogTitle className="font-display font-black tracking-tight text-2xl">Log Activity</DialogTitle>
            <DialogDescription className="sr-only">Record a BD interaction for a physician.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Physician *</Label>
              <Select value={form.physician_id} onValueChange={(v) => setForm({...form, physician_id: v})}>
                <SelectTrigger data-testid="activity-physician" className="rounded-none mt-1"><SelectValue placeholder="Select physician" /></SelectTrigger>
                <SelectContent>
                  {physicians.map(p => <SelectItem key={p.id} value={p.id}>{p.full_name} {p.practice_name && `(${p.practice_name})`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Activity Type *</Label>
                <Select value={form.activity_type} onValueChange={(v) => setForm({...form, activity_type: v})}>
                  <SelectTrigger data-testid="activity-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Date</Label>
                <Input type="datetime-local" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="rounded-none mt-1 font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">BD Rep</Label>
              <Input data-testid="activity-rep" value={form.bd_rep} onChange={(e) => setForm({...form, bd_rep: e.target.value})} placeholder="Rep #1" className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Notes</Label>
              <Textarea data-testid="activity-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className="rounded-none mt-1" placeholder="What happened?" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-[#5C5E66]">Next Step</Label>
              <Input data-testid="activity-next" value={form.next_step} onChange={(e) => setForm({...form, next_step: e.target.value})} className="rounded-none mt-1" placeholder="e.g. Send financial model" />
            </div>
          </div>
          <DialogFooter className="border-t border-[#E2E2E5] pt-3">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-activity-btn" onClick={save} className="bg-[#006CFF] hover:bg-[#0052CC] text-white rounded-none">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhysicianDetailSheet
        physicianId={selectedDetail}
        open={!!selectedDetail}
        onOpenChange={(o) => !o && setSelectedDetail(null)}
        onUpdated={load}
      />
    </div>
  );
}

const Th = ({ children }) => <div className="px-4 py-2.5 text-[10px] font-mono uppercase tracking-wider text-[#5C5E66]">{children}</div>;
const Td = ({ children, className = "", onClick }) => <div onClick={onClick} className={`px-4 py-3 ${className}`}>{children}</div>;
